/**
 * Status terminal da etiqueta → campo do agregado `daily_ops` (issue #351).
 *
 * ## Por que este módulo existe
 *
 * A tabela "status terminal → contador" estava redigitada em nove lugares: o
 * trigger que ESCREVE o agregado (`onLabelWritten`), o job que o AUDITA
 * (`monitorDailyOpsHealth`) e sete scripts de reparo/diagnóstico. Quando o
 * épico #323 acrescentou `MIXED`, dois dos três mudaram — e
 * `scripts/repair-daily-ops.ts`, que é ferramenta de ESCRITA em produção,
 * ficou para trás: uma etiqueta `MIXED` não entrava em nenhum contador, então
 * a "verdade" que ele recomputa era menor que a real e um `--apply` apagaria
 * baixa legítima. `daily_ops` é append-only por delta e não tem segunda fonte:
 * essa escrita não se desfaz.
 *
 * Daí a regra: **quem soma, repara ou audita `daily_ops` importa daqui**, e
 * nunca redigita a lista. `dailyOpsTerminals.test.ts` confere a tabela contra
 * `LABEL_CLOSED_STATUSES` (`labelMutability.ts`), então um status terminal novo
 * reprova o build em vez de passar despercebido por uma ferramenta que escreve.
 *
 * Sem `zod` de propósito, como `labelMutability`: os scripts do repo importam
 * este módulo com `tsx`, direto do source.
 */

import { LabelStatus } from './enums';

/** Status em que a etiqueta é uma baixa consumada — os que somam `dropped`. */
export type LabelTerminalStatus = Extract<
  LabelStatus,
  'CONSUMED' | 'DISCARDED' | 'EXPIRED' | 'MIXED'
>;

/** Contadores granulares do `dropped` em `daily_ops.totals`. */
export type DailyOpsTerminalField = 'consumed' | 'discarded' | 'expired' | 'mixed';

/**
 * Os quatro termos do `dropped`. A ORDEM é a de leitura dos relatórios
 * (desfechos simples, depois o misto); nada depende dela, mas mantê-la estável
 * mantém a saída dos scripts comparável entre execuções.
 */
export const DAILY_OPS_TERMINAL_FIELDS: readonly DailyOpsTerminalField[] = [
  'consumed',
  'discarded',
  'expired',
  'mixed',
];

/**
 * A tabela. `MIXED` (épico #323) é o quarto desfecho: a etiqueta fechada por
 * duas ou mais causas conta UMA vez, no campo `mixed`, e nunca nos campos das
 * causas que a compõem — a decomposição por quantidade vive em
 * `byProduct.*Qty`, num eixo diferente.
 */
export const TERMINAL_STATUS_TO_DAILY_OPS_FIELD: Readonly<
  Record<LabelTerminalStatus, DailyOpsTerminalField>
> = {
  [LabelStatus.CONSUMED]: 'consumed',
  [LabelStatus.DISCARDED]: 'discarded',
  [LabelStatus.EXPIRED]: 'expired',
  [LabelStatus.MIXED]: 'mixed',
};

/**
 * Campo do `daily_ops` para um status vindo do wire (`string`, nunca enum — o
 * doc pode trazer valor desconhecido). `null` = o status não é terminal e não
 * move contador de baixa.
 */
export function dailyOpsFieldForStatus(status: string): DailyOpsTerminalField | null {
  // `Object.hasOwn` em vez de indexar e cair no `??`: `status` vem do wire, e
  // `'constructor'`/`'toString'` resolveriam pelo prototype, devolvendo uma
  // função onde o chamador espera um campo do agregado.
  if (!Object.hasOwn(TERMINAL_STATUS_TO_DAILY_OPS_FIELD, status)) return null;
  return TERMINAL_STATUS_TO_DAILY_OPS_FIELD[status as LabelTerminalStatus];
}

/**
 * O inverso de `TERMINAL_STATUS_TO_DAILY_OPS_FIELD`, **derivado** dela e nunca
 * redigitado: quem parte de um campo divergente do `daily_ops` e precisa do
 * status para ir buscar as etiquetas (o `monitorDailyOpsHealth` faz isso na
 * segunda passada) tem de usar a mesma tabela do caminho de ida. Duas tabelas
 * escritas à mão discordarem foi a #351.
 */
export const DAILY_OPS_FIELD_TO_TERMINAL_STATUS: Readonly<
  Record<DailyOpsTerminalField, LabelTerminalStatus>
> = Object.fromEntries(
  Object.entries(TERMINAL_STATUS_TO_DAILY_OPS_FIELD).map(([status, field]) => [field, status]),
) as Record<DailyOpsTerminalField, LabelTerminalStatus>;

/** `true` para os status que somam `dropped`. */
export function isTerminalLabelStatus(status: string): status is LabelTerminalStatus {
  return dailyOpsFieldForStatus(status) !== null;
}

/**
 * `true` se esta etiqueta entra no agregado `daily_ops`.
 *
 * Etiqueta sem `responsibleId` é descartada do agregado INTEIRO — `totals`,
 * `byResponsible` e `byActor`. É a "divergência 3" de `docs/label-counters.md`:
 * `responsibleId` é obrigatório nas rules desde a Sprint 27, então isso alcança
 * só documento legado, e o efeito é A ≥ B, sistematicamente.
 *
 * **Quem recompõe `daily_ops` a partir de `labels` importa daqui.** A regra
 * morava só dentro do trigger, e o preço de mantê-la em corpos separados já foi
 * cobrado duas vezes: a #419 (duas projeções do escritor discordando entre si) e
 * as duas bordas da #423. Os recompositores contavam TODAS as baixas, e numa
 * etiqueta sem responsável o escritor não creditava e o auditor creditava:
 * `DRIFT` esperado por desenho, com a resposta natural (`repair --apply`)
 * gravando em `totals` justamente o que o trigger descartou de propósito.
 *
 * **A lista de quem importa daqui vive em `docs/label-counters.md` §3**, e não
 * nesta docstring: em três dias ela foi de três a seis ferramentas, e cada
 * rodada deixou a contagem desatualizada em algum comentário (#425).
 *
 * **O parâmetro é `unknown` de propósito, e nenhuma query do Firestore
 * substitui esta função.** O valor vem do wire e a exclusão alcança quatro
 * casos, não um:
 *
 * | `responsibleId` | aqui | `where('responsibleId', '!=', '')` |
 * | --- | --- | --- |
 * | ausente | exclui | exclui |
 * | `''` | exclui | exclui |
 * | `null` | exclui | **inclui** |
 * | não-string | exclui | **inclui** |
 * | `'r1'` | inclui | inclui |
 *
 * São `null` e não-string que tornam o filtro no servidor impossível — não o
 * campo ausente, que o `!=` acerta. Somem-se o índice composto novo que aquele
 * filtro exigiria e o custo dele no caminho feliz, para uma condição hoje
 * medida em zero, e o lugar certo do predicado é aqui.
 *
 * **Cuidado no call site:** como o parâmetro é opcional, passar um objeto
 * PROJETADO que nunca carregou `responsibleId` compila e exclui **tudo**, em
 * silêncio. Quem recompõe a partir de um `d.data()` cru está seguro; quem
 * monta uma linha própria (o `LabelRow` de `scripts/diag-audit-baixas.ts` fazia
 * isso) precisa incluir o campo na projeção.
 */
export function countsInDailyOps(label: { readonly responsibleId?: unknown }): boolean {
  return typeof label.responsibleId === 'string' && label.responsibleId.length > 0;
}

/** `dropped` mais os quatro granulares — o que uma ferramenta de reparo toca. */
export type DailyOpsRepairableField = 'dropped' | DailyOpsTerminalField;

/**
 * `created`/`printed` ficam DE FORA de propósito: para eles a verdade
 * recomputada a partir de `labels` é um LOWER BOUND (o delete físico anterior à
 * #148 levou o doc junto), e reparar destruiria histórico irrecuperável.
 */
export const DAILY_OPS_REPAIRABLE_FIELDS: readonly DailyOpsRepairableField[] = [
  'dropped',
  ...DAILY_OPS_TERMINAL_FIELDS,
];

/**
 * A definição do agregado: `dropped === consumed + discarded + expired + mixed`.
 * É a igualdade que `monitorDailyOpsHealth` cobra e que o reparo tem de
 * produzir — as duas ferramentas divergirem sobre ela foi a #351.
 */
export function computeDropped(
  counts: Readonly<Partial<Record<DailyOpsTerminalField, number>>>,
): number {
  let total = 0;
  for (const field of DAILY_OPS_TERMINAL_FIELDS) total += counts[field] ?? 0;
  return total;
}

/** Acumulador zerado com TODOS os campos reparáveis — sem literal solto. */
export function zeroRepairableCounts(): Record<DailyOpsRepairableField, number> {
  const zero = {} as Record<DailyOpsRepairableField, number>;
  for (const field of DAILY_OPS_REPAIRABLE_FIELDS) zero[field] = 0;
  return zero;
}
